import { useState, useEffect } from 'react';

interface AIModalProps {
  action: string | null;
  onClose: () => void;
  onApply: (result: string) => void;
}

const actionConfig: Record<string, { title: string; description: string; steps: string[] }> = {
  'generate-layout': {
    title: 'Gerar Layout Automático',
    description: 'A IA está analisando os artigos disponíveis e criando um layout equilibrado para a página.',
    steps: ['Analisando artigos disponíveis...', 'Calculando hierarquia visual...', 'Posicionando blocos...', 'Ajustando proporções...', 'Layout gerado com sucesso!'],
  },
  'generate-front-page': {
    title: 'Criar Capa Automática',
    description: 'Gerando capa editorial com as principais manchetes do dia.',
    steps: ['Selecionando manchetes principais...', 'Definindo hierarquia de notícias...', 'Posicionando foto de destaque...', 'Adicionando chamadas secundárias...', 'Capa criada com sucesso!'],
  },
  'summarize': {
    title: 'Resumir Artigos',
    description: 'Condensando textos longos para melhor aproveitamento do espaço editorial.',
    steps: ['Identificando artigos longos...', 'Extraindo pontos principais...', 'Gerando resumos...', 'Revisando coerência...', 'Resumos prontos!'],
  },
  'generate-headlines': {
    title: 'Gerar Manchetes',
    description: 'Criando títulos impactantes e otimizados para cada artigo.',
    steps: ['Analisando conteúdo dos artigos...', 'Identificando palavras-chave...', 'Gerando variações de títulos...', 'Selecionando melhores opções...', 'Manchetes geradas!'],
  },
  'suggest-images': {
    title: 'Sugerir Imagens',
    description: 'Recomendando imagens relevantes para cada artigo baseado no contexto.',
    steps: ['Analisando temas dos artigos...', 'Buscando imagens relacionadas...', 'Avaliando relevância visual...', 'Organizando sugestões...', 'Imagens sugeridas!'],
  },
  'optimize-balance': {
    title: 'Otimizar Equilíbrio',
    description: 'Balanceando espaços, pesos visuais e hierarquia da página.',
    steps: ['Analisando distribuição atual...', 'Calculando pesos visuais...', 'Ajustando proporções...', 'Verificando regra dos terços...', 'Layout otimizado!'],
  },
  'check-grammar': {
    title: 'Revisar Gramática',
    description: 'Verificando e corrigindo erros gramaticais em todos os textos.',
    steps: ['Escaneando textos...', 'Identificando erros...', 'Aplicando correções...', 'Verificando coerência...', 'Revisão concluída!'],
  },
  'generate-subheadlines': {
    title: 'Gerar Subtítulos',
    description: 'Criando subtítulos complementares para enriquecer os artigos.',
    steps: ['Analisando manchetes...', 'Identificando contexto...', 'Gerando subtítulos...', 'Ajustando tom editorial...', 'Subtítulos prontos!'],
  },
};

const mockResults: Record<string, string> = {
  'generate-layout': 'Layout gerado com 6 blocos: 1 manchete principal, 2 artigos secundários, 1 foto de destaque e 2 chamadas de rodapé.',
  'generate-front-page': 'Capa criada com manchete "Prefeitura anuncia plano de mobilidade urbana", foto de destaque e 3 chamadas secundárias.',
  'summarize': '4 artigos resumidos. Redução média de 42% no número de palavras mantendo os pontos principais.',
  'generate-headlines': '6 manchetes geradas. Sugestão principal: "Jardel City investe R$ 2,4 bi em mobilidade urbana para 2026".',
  'suggest-images': '12 imagens sugeridas para 6 artigos. Todas com alta relevância contextual.',
  'optimize-balance': 'Layout rebalanceado. Hierarquia visual melhorada em 34%. Regra dos terços aplicada.',
  'check-grammar': '3 erros corrigidos em 2 artigos. Texto revisado e aprovado.',
  'generate-subheadlines': '6 subtítulos gerados. Exemplo: "Investimento de R$ 2,4 bilhões prevê 15 novos corredores de ônibus".',
};

export default function AIModal({ action, onClose, onApply }: AIModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const config = action ? actionConfig[action] : null;

  useEffect(() => {
    if (!action || !config) return;
    setCurrentStep(0);
    setIsComplete(false);

    const steps = config.steps;
    let step = 0;

    const interval = setInterval(() => {
      step += 1;
      setCurrentStep(step);
      if (step >= steps.length - 1) {
        clearInterval(interval);
        setIsComplete(true);
      }
    }, 700);

    return () => clearInterval(interval);
  }, [action]);

  if (!action || !config) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-[#111111]">
          <div className="w-8 h-8 flex items-center justify-center bg-[#111111]">
            <i className="ri-robot-line text-white text-sm"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-[#111111] font-serif">{config.title}</p>
            <p className="text-[10px] text-[#999999] font-sans">Motor de IA — Jardel City Post</p>
          </div>
          {!isComplete && (
            <div className="w-5 h-5 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>

        {/* Body */}
        <div className="p-5">
          <p className="text-xs text-[#555555] font-sans mb-4">{config.description}</p>

          {/* Steps */}
          <div className="space-y-2 mb-4">
            {config.steps.map((step, i) => (
              <div key={i} className={`flex items-center gap-2.5 transition-all ${i > currentStep ? 'opacity-30' : ''}`}>
                <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${
                  i < currentStep ? 'bg-[#1a7a1a]' :
                  i === currentStep ? 'bg-[#111111]' :
                  'bg-[#E0E0E0]'
                }`}>
                  {i < currentStep ? (
                    <i className="ri-check-line text-white text-[8px]"></i>
                  ) : i === currentStep ? (
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                  ) : null}
                </div>
                <p className={`text-[10px] font-sans ${i <= currentStep ? 'text-[#111111] font-semibold' : 'text-[#999999]'}`}>
                  {step}
                </p>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="w-full bg-[#F0F0F0] h-1 mb-4">
            <div
              className="bg-[#111111] h-1 transition-all duration-500"
              style={{ width: `${(currentStep / (config.steps.length - 1)) * 100}%` }}
            ></div>
          </div>

          {/* Result */}
          {isComplete && action && mockResults[action] && (
            <div className="border-l-4 border-[#1a7a1a] bg-[#F0FFF0] px-3 py-2 mb-4">
              <p className="text-[10px] font-bold text-[#1a7a1a] font-sans uppercase tracking-wide mb-0.5">Resultado</p>
              <p className="text-xs text-[#111111] font-sans">{mockResults[action]}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-[#E0E0E0] text-xs font-sans text-[#444444] hover:bg-[#F0F0F0] cursor-pointer transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => { onApply(action || ''); onClose(); }}
            disabled={!isComplete}
            className={`flex-1 py-2 text-xs font-sans font-bold uppercase tracking-wide cursor-pointer transition-colors ${
              isComplete
                ? 'bg-[#111111] text-white hover:bg-[#333333]'
                : 'bg-[#E0E0E0] text-[#999999] cursor-not-allowed'
            }`}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
